import { PopupCloseCallback, PopupOpenCallback, PopupProperties } from "../models/popup.models";
import { isFormSubmissionSuccess } from "./forms.utility";
import { QueriesUtility } from "./queries.utility";
import { UrlUtility } from "./url.utility";

/**
 * PopupUtility opens and manages Telerik RadWindow popup dialogs (iMIS's modal window mechanism), including reacting to form submission results, hooking into a popup's iframe load events such as a panel editor load window, and opening the query editor popup by query path.
 */
export class PopupUtility {

    private static DEFAULT_WINDOW: string = "GenericWindow";

    /**
     * Opens a Telerik RadWindow popup dialog pointing at the given URL, appending the supplied query parameters and applying the popup's size, title, name, and close callback.
     * @param properties Configuration for the popup, including its URL, query params, title, size, window name, and close callback (defaults applied via {@link PopupProperties})
     */
    public static createPopup(properties: PopupProperties) {
        let propertiesWithDefaults = Object.assign(new PopupProperties(), properties);
        let urlWithParams = UrlUtility.getUrlStringWithParams(propertiesWithDefaults.url, propertiesWithDefaults.params);

        window.ShowDialog(urlWithParams, null, propertiesWithDefaults.width, propertiesWithDefaults.height, propertiesWithDefaults.title, null, "E", null, propertiesWithDefaults.windowName, true, false, propertiesWithDefaults.callback ? (sender, args) => propertiesWithDefaults.callback(sender, args) : null);
    }

    /**
     * Closes the current Telerik RadWindow if the form it contains was submitted successfully, passing a value back to the parent window's close callback.
     * @param value The value passed to the RadWindow's close callback when the popup is closed (defaults to true)
     */
    public static closePopupOnFormSubmissionSuccess(value: any = true) {
        if (isFormSubmissionSuccess()) {
            let currentWindow = window.GetRadWindow();
            currentWindow.close(value);
        }
    }

    /**
     * Looks up an open Telerik RadWindow popup by its registered window name.
     * @param windowName The name of the RadWindow to look up
     * @returns The matching RadWindow instance
     */
    public static getPopupWindowByName(windowName: string): Telerik.Web.UI.RadWindow {
        return window.GetRadWindowManager().getWindowByName(windowName);
    }

    /**
     * Registers a callback to run whenever the given popup window's content iframe finishes loading, re-attaching the load listener each time the popup is shown and detaching it (along with the show/close hooks) once the popup closes.
     * @param callback Invoked with the RadWindow instance and its content iframe each time the iframe finishes loading
     * @param popupWindowName The registered name of the popup window to observe (defaults to the shared "GenericWindow" popup)
     * @returns The internal "show" callback that was registered, which can be passed to {@link removePopupWindowFrameLoad} to unregister it
     */
    public static addPopupWindowFrameLoad(callback: (sender: Telerik.Web.UI.RadWindow, iframe: HTMLIFrameElement) => void, popupWindowName: string = this.DEFAULT_WINDOW): PopupOpenCallback {
        let window = this.getPopupWindowByName(popupWindowName);

        let iFrameLoadCallback;
        let closeCallback: PopupCloseCallback = (sender, eventArgs) => {
            let jIframe = jQuery(sender.get_contentFrame());
            jIframe.off("load", iFrameLoadCallback);

            window.remove_close(closeCallback);
        };
        let showCallback: PopupOpenCallback = (sender, eventArgs) => {
            let jIframe = jQuery(sender.get_contentFrame());

            if (iFrameLoadCallback == null) {
                iFrameLoadCallback = function () {
                    callback(sender, this);
                    window.add_close(closeCallback);
                };
            }

            jIframe.off("load", iFrameLoadCallback).on("load", iFrameLoadCallback);
        };
        window.add_show(showCallback);

        return showCallback;
    }

    /**
     * Unregisters a "show" callback previously added by {@link addPopupWindowFrameLoad} from the given popup window.
     * @param showCallback The callback returned by {@link addPopupWindowFrameLoad} to remove
     * @param popupWindowName The registered name of the popup window the callback was attached to (defaults to the shared "GenericWindow" popup)
     */
    public static removePopupWindowFrameLoad(showCallback: PopupOpenCallback, popupWindowName: string = this.DEFAULT_WINDOW) {
        this.getPopupWindowByName(popupWindowName).remove_show(showCallback);
    }

    /**
     * Registers a popup-window-frame-load handler that detects when the panel editor dialog (PanelEditDialog.aspx) opens, extracts the panel's mode, identity, and source table/field metadata from the loaded iframe, and invokes the given callback with that information.
     * @param callback Function invoked with the popup sender, the loaded iframe, and the parsed panel properties (panelDefinitionId, mode, identity, sourceFields) once the panel editor popup has loaded.
     * @returns The popup-open callback reference registered with PopupUtility, which can be used to remove the handler later.
     */
    // TODO: this needs refactoring!
    public static addPanelEditorPopupOpen(callback: (sender: Telerik.Web.UI.RadWindow, iframe: HTMLIFrameElement, panelProperties) => void): PopupOpenCallback {
        /**
         * Scans the panel editor iframe's DOM for editable and read-only panel fields, parsing each field's element ID to determine its source table and field name.
         * @param iframeContentWindow The content window of the panel editor iframe to scan for field elements; defaults to the current window.
         * @returns An array of field property objects (type, tableName, fieldName, fieldSelector) for each detected panel field.
         */
        let getPanelEditorFields = function (iframeContentWindow: Window = window) {
            let sourceFields = [];

            let allLabelOrReadOnlyFieldSelectors = ".PanelField label[for], .PanelFieldValue > span[id]:first-child";
            let jFields = iframeContentWindow.jQuery(allLabelOrReadOnlyFieldSelectors);

            let panelFieldValueChildSelector = ".PanelFieldValue > :first-child";
            let jFieldIds = iframeContentWindow.jQuery(panelFieldValueChildSelector);

            // get field properties
            jFields.each(function (index) {
                let jField = jQuery(this);
                let forAttrValue = jField.attr("for");
                let fieldProperties = {
                    type: "EDIT",
                    tableName: null,
                    fieldName: null,
                    fieldSelector: forAttrValue != null ? "#" + forAttrValue : null
                };

                // readonly fields will be caught using the ID selector rather than a label tag with a FOR attribute
                let fieldId = forAttrValue;
                let idAttrValue = jField.attr("id");
                if (idAttrValue != null) {
                    fieldProperties.type = "READONLY";
                    fieldId = idAttrValue;
                }

                // ensure the selected field id matches that from the fieldId selector, prioritize fieldId value if it exists
                let fieldIdElement = jFieldIds[index];
                let fieldIdElementId = fieldIdElement.id;
                if (fieldIdElementId.length > 0 && fieldIdElementId != fieldId) {
                    fieldId = fieldIdElementId;
                }

                // parse the field id to extract information using a common format
                let searchRegex = /_PanelFieldEditor_(.+?)(___|\.)(.+?)$/;
                let match = fieldId.match(searchRegex);
                if (match.length == 4) {
                    // this is a valid match because it has all the expected capture groups (3 of them)
                    fieldProperties.tableName = match[1];
                    fieldProperties.fieldName = match[3];
                }

                sourceFields.push(fieldProperties);
            });

            return sourceFields;
        };
        /**
         * Groups a flat list of panel editor field properties into an object keyed by table name, with each entry holding the table name and its associated fields.
         * @param sourceFields The array of field property objects produced by getPanelEditorFields.
         * @returns An object mapping table names to their grouped field definitions.
         */
        let groupPanelEditorFields = function (sourceFields) {
            let sources = {};

            sourceFields.forEach(function (field) {
                let tableName = field.tableName;
                sources[tableName] = Object.assign({
                    tableName: tableName,
                    fields: []
                }, sources[tableName]);

                sources[tableName].fields.push(field);
            });

            return sources;
        };

        let popUpOpenCallbackRef = this.addPopupWindowFrameLoad(function (sender, iframe) {
            // ensure url is of a panel editor page
            let iframeSrc = iframe.src;
            if (iframeSrc.indexOf("/PanelEditDialog.aspx") != -1) {
                const MODE = { CREATE: "CREATE", EDIT: "EDIT" };

                let iframeContentWindow = iframe.contentWindow;
                let VAIMISUtilityV2ContentWindow = iframeContentWindow.VAIMISUtilityV2;
                let panelDefinitionId = VAIMISUtilityV2ContentWindow.Url.findUrlParameter("PanelDefinitionId");
                let key = VAIMISUtilityV2ContentWindow.Url.findUrlParameter("iKey");

                // parse key field to extract ID/SEQN properties if any
                let mode = key == "-1" ? MODE.CREATE : MODE.EDIT;
                let identity = [];
                if (mode == MODE.EDIT) {
                    // get id/seqn values
                    let keyParts = key.slice(1).split("|");
                    identity = keyParts;
                }

                // get all sources and fields, organize fields by their sources
                let panelEditorFields = getPanelEditorFields(iframeContentWindow);
                let panelEditorFieldsObject = groupPanelEditorFields(panelEditorFields);
                let sourceFields = Object.keys(panelEditorFieldsObject).map(function (key) { return panelEditorFieldsObject[key]; });

                let panelProperties = { panelDefinitionId: panelDefinitionId, mode: mode, identity: identity, sourceFields: sourceFields };
                callback(sender, iframe, panelProperties);
            }
        });

        return popUpOpenCallbackRef;
    }

    /**
     * Opens the iMIS Query Builder popup for the query at the given path, temporarily enabling REST access on the query definition if it isn't already enabled so it can be looked up, and restoring the previous access setting afterward.
     * @param queryPath The path of the query to open in the query editor.
     */
    public static async openQueryEditorByPath(queryPath: string) {
        // check if query has rest access
        let queryRestAccess = await QueriesUtility.getQueryRestAccessByPath(queryPath);
        if (queryRestAccess == null) {
            throw new Error("Query access request failed for path: " + queryPath);
        }
        if (!queryRestAccess) {
            // set rest access
            let isSuccess = await QueriesUtility.setQueryRestAccessByPath(queryPath, true);
            if (!isSuccess) {
                throw new Error("Query rest access update failed for path: " + queryPath);
            }
        }

        let queryDefinition = await QueriesUtility.getQueryDefinitionByPath(queryPath);
        if (queryDefinition != null) {
            let windowName = `QueryEditor_${queryDefinition.QueryDefinitionId}`;
            this.createPopup({
                title: queryPath,
                url: `/iMIS/QueryBuilder/Design.aspx?iMode=Edit&TemplateType=E&iUniformKey=${queryDefinition.QueryDefinitionId}&iOperation=Edit`,
                width: "90%",
                height: "90%",
                windowName: windowName,
                callback: (sender, args) => {
                    let popupWindow = window.GetRadWindowManager().getWindowByName(windowName);
                    if (popupWindow) {
                        window.GetRadWindowManager()["removeWindow"](popupWindow);
                    }
                }
            });
        } else {
            let errorMessage = "Query not found for path: " + queryPath;

            console.error(errorMessage);
            throw errorMessage;
        }

        // ensure query access is reset
        if (!queryRestAccess) {
            let isSuccess = await QueriesUtility.setQueryRestAccessByPath(queryPath, false);
            if (!isSuccess) {
                console.error("Query rest access reset failed for path: " + queryPath);
            }
        }
    }

}